import React, { createContext, useContext, useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { authService, UserProfile } from '../services/authService';
import { auth } from '../firebase/config';
import { reload } from 'firebase/auth';
import { ExtendedUserProfile, UserRole, AgentType, UserPermissions } from '../types/auth';
import { permissionService } from '../services/permissionService';

interface AuthContextType {
  currentUser: User | null;
  userProfile: ExtendedUserProfile | null;
  loading: boolean;
  userPermissions: UserPermissions | null;
  selectedAgent: AgentType | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  sendPasswordResetEmail: (email: string) => Promise<void>;
  updateProfile: (displayName: string) => Promise<void>;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  deleteAccount: (password: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  selectAgent: (agentType: AgentType) => void;
  getUserRole: (email: string) => UserRole;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<ExtendedUserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [userPermissions, setUserPermissions] = useState<UserPermissions | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<AgentType | null>(null);

  // Function to determine user role based on domain
  const getUserRole = (): UserRole => {
    // Use domain-based role detection instead of email-based
    return permissionService.detectUserRole();
  };

  // Function to select agent
  const selectAgent = (agentType: AgentType) => {
    setSelectedAgent(agentType);
    if (userProfile) {
      const updatedProfile = { ...userProfile, selectedAgent: agentType };
      setUserProfile(updatedProfile);
      
      // Update permissions based on role and selected agent
      const permissions = permissionService.getUserPermissions(updatedProfile.role, agentType);
      setUserPermissions(permissions);
    }
  };

  useEffect(() => {
    // Set up the auth state listener
    const unsubscribe = authService.onAuthStateChange((user) => {
      console.log('Auth state changed:', user?.email);
      setCurrentUser(user);
      
      if (user) {
        const role = getUserRole();
        const profile: ExtendedUserProfile = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          emailVerified: user.emailVerified,
          role
        };
        console.log('Setting user profile:', profile);
        setUserProfile(profile);
        
        // Set initial permissions
        const permissions = permissionService.getUserPermissions(role);
        setUserPermissions(permissions);
      } else {
        console.log('Clearing user profile');
        setUserProfile(null);
        setUserPermissions(null);
        setSelectedAgent(null);
      }
      
      setLoading(false);
    });

    // Clean up the listener on unmount
    return () => {
      console.log('Cleaning up auth state listener');
      unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const profile = await authService.login(email, password);
      const role = getUserRole();
      const extendedProfile: ExtendedUserProfile = {
        ...profile,
        role
      };
      setUserProfile(extendedProfile);
      
      // Set initial permissions
      const permissions = permissionService.getUserPermissions(role);
      setUserPermissions(permissions);
    } finally {
      setLoading(false);
    }
  };

  const register = async (email: string, password: string, displayName: string) => {
    setLoading(true);
    try {
      const profile = await authService.register(email, password, displayName);
      const role = getUserRole();
      const extendedProfile: ExtendedUserProfile = {
        ...profile,
        role
      };
      setUserProfile(extendedProfile);
      
      // Set initial permissions
      const permissions = permissionService.getUserPermissions(role);
      setUserPermissions(permissions);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await authService.logout();
      setUserProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const sendVerificationEmail = async () => {
    setLoading(true);
    try {
      if (!currentUser) {
        throw new Error('No user is currently signed in');
      }
      await authService.sendVerificationEmail();
    } catch (error) {
      console.error('Error sending verification email:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const sendPasswordResetEmail = async (email: string) => {
    setLoading(true);
    try {
      await authService.sendPasswordResetEmail(email);
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (displayName: string) => {
    setLoading(true);
    try {
      await authService.updateProfile(displayName);
      if (userProfile) {
        setUserProfile({
          ...userProfile,
          displayName
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async (currentPassword: string, newPassword: string) => {
    setLoading(true);
    try {
      await authService.updatePassword(currentPassword, newPassword);
    } finally {
      setLoading(false);
    }
  };

  const deleteAccount = async (password: string) => {
    setLoading(true);
    try {
      await authService.deleteAccount(password);
      setUserProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    setLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        setCurrentUser(null);
        setUserProfile(null);
        setUserPermissions(null);
        setSelectedAgent(null);
        return;
      }
      await reload(user);
      const role = getUserRole();
      const profile: ExtendedUserProfile = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        emailVerified: user.emailVerified,
        role
      };
      setCurrentUser(user);
      setUserProfile(profile);
      
      // Set initial permissions
      const permissions = permissionService.getUserPermissions(role);
      setUserPermissions(permissions);
    } catch (error) {
      console.error('Error refreshing user:', error);
      setCurrentUser(null);
      setUserProfile(null);
      setUserPermissions(null);
      setSelectedAgent(null);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    currentUser,
    userProfile,
    loading,
    login,
    register,
    logout,
    sendVerificationEmail,
    sendPasswordResetEmail,
    updateProfile,
    updatePassword,
    deleteAccount,
    refreshUser,
    userPermissions,
    selectedAgent,
    selectAgent,
    getUserRole
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 